import { useCallback, useEffect, useState, useMemo } from 'react';
import { isClient } from 'legacyfrontend/helpers/ssr-helper';
import { useTranslation } from 'react-i18next';
import { HiShare } from 'react-icons/hi';
import { WhatsappShareButton, WhatsappIcon } from 'react-share';
import { isValid, isSameDay, isBefore, parseISO } from 'date-fns';
import { formatWithMomentFormat } from 'legacyfrontend/utils/dateUtils';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  Rating,
  Tooltip,
  Stack,
  Modal,
  Box,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

// Components
import Flex from 'legacyfrontend/components/Flex';
import LoadSpinner from 'legacyfrontend/components/spinner';
import ImageGallery from './components/ImageGallery';
import PackageItinerary from './components/PackageItinerary';
import DynamicIncludesRow from 'legacyfrontend/components/packages/DynamicIncludesRow';
import PaymentSummaryRow from './components/PaymentSummaryRow';
import ContactFormModal from '../../components/ContactFormModal';
import ShareButton from '../../components/ui/ShareButton';

// Utils & Constants
import store from 'storeClient';
import paquetesApi from 'legacyfrontend/api/paquetes';
import { DATE_KEYS, FALLBACK_IMAGE } from 'legacyfrontend/constants';
import getFinalPrice from 'legacyfrontend/utils/get-final-price';
import { useColors } from 'legacyfrontend/hooks/useColors';

// Styles
import 'styles/components/paquete-detalle.css';

const PackageAccordion = ({ title, content, defaultExpanded = false }) => {
  if (!content?.trim()) return null;

  // Función para limpiar guiones manuales en listas
  const cleanListContent = (htmlContent) => {
    return htmlContent
      .replace(/<li[^>]*>\s*-\s*/g, '<li>') // Remover guiones al inicio de <li>
      .replace(/<li[^>]*><span[^>]*>\s*-\s*/g, '<li><span>'); // Remover guiones dentro de spans en <li>
  };

  const cleanedContent = cleanListContent(content);

  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ border: 'none' }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${title.toLowerCase()}-content`}
        id={`${title.toLowerCase()}-header`}
      >
        <Typography className='package-descriptions__title' sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box
          dangerouslySetInnerHTML={{ __html: cleanedContent }}
          sx={{
            '& p': {
              marginBottom: '1rem',
              lineHeight: 1.6,
            },
            '& ul': {
              paddingLeft: '1.5rem',
              marginBottom: '1rem',
              listStyleType: 'disc !important',
              listStylePosition: 'outside !important',
            },
            '& ol': {
              paddingLeft: '1.5rem',
              marginBottom: '1rem',
              listStyleType: 'decimal !important',
              listStylePosition: 'outside !important',
            },
            '& li': {
              marginBottom: '0.5rem',
              display: 'list-item !important',
              paddingLeft: '0.25rem',
              listStyleType: 'inherit !important',
            },
            '& ul > li': {
              listStyleType: 'disc !important',
            },
            '& ol > li': {
              listStyleType: 'decimal !important',
            },
            '& span': { display: 'inline' },
            '& a': { color: '#e60000', textDecoration: 'underline' },
            '& strong': { fontWeight: 'bold' },
            lineHeight: 1.6,
            fontSize: '14px',
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

const parseSalidasUrl = (searchString) => {
  try {
    const searchParams = new URLSearchParams(
      searchString.startsWith('?') ? searchString : `?${searchString}`
    );
    return {
      id: searchParams.get('id'),
      salidaId: searchParams.get('salidaId'),
    };
  } catch (error) {
    console.error('Error parsing salidas URL:', error);
    return { id: null, salidaId: null };
  }
};

const InternalPackageDetail = (props) => {
  const { t } = useTranslation('layout');
  const { background: mbrBackground, defaultBackground } = useColors();
  const [packageData, setPackageData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [consultaPackage, setConsultaPackage] = useState();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [urlComplete, setUrlComplete] = useState('');
  const [selectedPrice, setSelectedPrice] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const { token } = store.getState().applicationState;
  const currencyCode =
    packageData?.currencyAplicacion?.codigo ||
    store.getState().applicationState?.currency?.code ||
    'USD';

  const packageInfo = packageData?.paqueteInformacion?.[0] || {};
  const hotelInfo = packageInfo?.hotelesInformacion?.[0] || {};
  const priceInfo = packageInfo?.precioInformacion?.[0] || {};
  const includedItems = packageData?.paquetesIncluido || [];
  const packageDescription = packageData?.paqueteDescripciones?.[0] || {};
  const packageImages = packageData?.paqueteImagenes || [];

  const { web } = store.getState().applicationState;
  const url = web === 'localhost' ? `http://localhost:${process.env.PORT}` : `https://${web}`;
  const urlIntranet = `${url}/paquetes${props.location.search}`;

  const sharePackage = useCallback(async () => {
    navigator.clipboard.writeText(urlComplete);
    setShowSnackbar(true);
  }, [urlComplete]);

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const departureDate = useMemo(() => {
    return packageInfo?.operationDateSince || null;
  }, [packageInfo]);

  const arrivalDate = useMemo(() => {
    return packageInfo?.operationDateUntil || null;
  }, [packageInfo]);

  const formattedDates = useMemo(() => {
    if (
      !departureDate ||
      !arrivalDate ||
      !isValid(parseISO(departureDate)) ||
      !isValid(parseISO(arrivalDate))
    ) {
      return '-';
    }

    const depDate = parseISO(departureDate);
    const arrDate = parseISO(arrivalDate);

    return isSameDay(depDate, arrDate)
      ? formatWithMomentFormat(depDate, DATE_KEYS.FORMAT.DAY_SHORT)
      : `${formatWithMomentFormat(depDate, DATE_KEYS.FORMAT.DAY_SHORT)} al ${formatWithMomentFormat(
          arrDate,
          DATE_KEYS.FORMAT.DAY_SHORT
        )}`;
  }, [departureDate, arrivalDate]);

  useEffect(() => {
    const fetchInternalPackage = async () => {
      try {
        setIsLoading(true);

        const isNewRoute = props.location.pathname.startsWith('/paquetes');
        let packageId = props.match.params.id;
        let salidaId = null;

        if (isNewRoute) {
          const { id, salidaId: extractedSalidaId } = parseSalidasUrl(props.location.search);
          packageId = id || props.match.params.id;
          salidaId = extractedSalidaId;
        }

        let data;
        try {
          data = await paquetesApi.getInternalPackage(packageId, token, salidaId);
        } catch (salidasError) {
          console.warn('Error con endpoint de salidas, fallback a endpoint:', salidasError);
          data = await paquetesApi.getInternalPackage(packageId, token);
        }

        setPackageData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching internal package:', err);
        setError(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInternalPackage();
  }, [props.match.params.id, props.location.pathname, props.location.search]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const currentUrl = isClient ? window.location.href : '';
      const isNewRoute = currentUrl.includes('/paquetes');

      let packageId = props.match.params.id;
      let salidaId = null;

      if (isNewRoute) {
        const { id, salidaId: extractedSalidaId } = parseSalidasUrl(props.location.search);
        packageId = id || props.match.params.id;
        salidaId = extractedSalidaId;
      }

      const baseUrl = isClient ? window.location.origin : '';
      const packshareUrl = `${baseUrl}/packshare/${packageId}${
        salidaId ? `?salidaId=${salidaId}` : ''
      }`;

      setUrlComplete(packshareUrl);
    }
  }, [props.match.params.id, props.location.pathname, props.location.search]);

  useEffect(() => {
    if (packageInfo?.precioInformacion?.length > 0) {
      setSelectedPrice(packageInfo.precioInformacion[0]);
    }
  }, [packageInfo]);

  useEffect(() => {
    isClient && window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    const handleMouseEnter = (e) => {
      const zoomIcon = e.target.parentElement.querySelector('.zoom-icon');
      if (zoomIcon) {
        zoomIcon.style.opacity = '1';
      }
    };

    const handleMouseLeave = (e) => {
      const zoomIcon = e.target.parentElement.querySelector('.zoom-icon');
      if (zoomIcon) {
        zoomIcon.style.opacity = '0';
      }
    };

    const images = document.querySelectorAll('.hotel-image');
    images.forEach((img) => {
      img.addEventListener('mouseenter', handleMouseEnter);
      img.addEventListener('mouseleave', handleMouseLeave);
    });

    return () => {
      images.forEach((img) => {
        img.removeEventListener('mouseenter', handleMouseEnter);
        img.removeEventListener('mouseleave', handleMouseLeave);
      });
    };
  }, [packageData]);

  if (isLoading) {
    return (
      <Stack
        sx={{
          flex: 1,
          height: '30rem',
          justifyContent: 'center',
          padding: '8rem 0',
          alignItems: 'center',
        }}
      >
        <LoadSpinner />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack
        sx={{
          flex: 1,
          height: '30rem',
          justifyContent: 'center',
          padding: '8rem 0',
          alignItems: 'center',
        }}
      >
        <p>Error loading package details</p>
      </Stack>
    );
  }

  return (
    <Flex className='package-detail-container p-4 gap-8' vertical>

      <Flex className='boxes-wrapper md:flex-row flex-col'>
        <Flex className='boxes-wrapper package-information-container' vertical>
          {packageImages.length > 0 && (
            <ImageGallery
              title={packageData?.title || ''}
              items={packageImages.map((img) => img?.imageUrl || FALLBACK_IMAGE)}
            />
          )}
          {packageDescription?.description && (
            <>
              <PackageAccordion
                title='Descripción'
                content={packageDescription.description}
                defaultExpanded
              />
            </>
          )}
          {packageData?.itinerarioDetalles && (
            <PackageItinerary itinerary={packageData.itinerarioDetalles} />
          )}
          <Card className='card-box' sx={{ padding: '1rem', marginBottom: '1rem' }}>
            <CardContent>
              <Typography
                variant='h6'
                sx={{ marginBottom: '1rem', fontWeight: 600, fontSize: '1rem' }}
              >
                Tarifas
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto' }}>
                <div style={{ minWidth: 800 }}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell size='small' />
                        <TableCell>Salidas disponibles</TableCell>
                        <TableCell>Base tarifaria</TableCell>
                        <TableCell align='right' size='small'>
                          Impuestos y Tasas
                        </TableCell>
                        <TableCell align='right' size='small'>
                          Precio Total
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {packageInfo?.precioInformacion?.map((precio) => (
                        <TableRow
                          key={precio?.id || Math.random()}
                          hover
                          onClick={() => setSelectedPrice(precio)}
                          selected={selectedPrice?.id === precio?.id}
                          sx={{
                            cursor: 'pointer',
                            '&.Mui-selected': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <TableCell size='small'>
                            <Radio
                              checked={selectedPrice?.id === precio?.id}
                              sx={{
                                color: mbrBackground,
                                '&.Mui-checked': {
                                  color: mbrBackground,
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography color='textSecondary' variant='body2'>
                              {formattedDates}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontWeight: 500 }} variant='body1'>
                              {typeof precio?.baseType?.description === 'string'
                                ? precio.baseType.description
                                : 'Tipo de base no especificado'}
                            </Typography>
                          </TableCell>
                          <TableCell align='center' size='small'>
                            <Typography className='price-value'>
                              {getFinalPrice({
                                currencyCode,
                                total: precio?.taxFree,
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell align='right' size='small'>
                            <Typography className='price-value'>
                              {getFinalPrice({
                                currencyCode,
                                total: precio?.totalAmount,
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TableContainer>
            </CardContent>
          </Card>
          {packageInfo?.hotelesInformacion?.length > 0 && (
            <Card className='card-box' sx={{ padding: '1rem', marginBottom: '1rem' }}>
              <CardContent>
                <Typography
                  variant='h6'
                  sx={{
                    marginBottom: '1rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Hoteles
                </Typography>
                <Flex style={{ gap: '1rem' }} vertical>
                  {packageInfo.hotelesInformacion.map((hotelInfo) => (
                    <Accordion
                      key={hotelInfo?.id || Math.random()}
                      sx={{
                        border: '1px solid #e0e0e0',
                        backgroundColor: '#fafafa',
                        borderRadius: '15px',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          '& .MuiAccordionSummary-content': {
                            margin: '12px 0',
                            width: '100%',
                          },
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={{ xs: 1, sm: 2 }}
                          sx={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: '1rem', sm: '1.25rem' },
                            }}
                            variant='h6'
                          >
                            {hotelInfo?.hotel?.name || 'Hotel sin nombre'}
                          </Typography>
                          <Rating
                            emptyIcon={<StarIcon fontSize='small' />}
                            icon={<StarIcon fontSize='small' />}
                            precision={0.5}
                            readOnly
                            value={hotelInfo?.hotel?.category || 0}
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Flex style={{ gap: '1rem' }} vertical>
                          {hotelInfo?.hotel?.hotelImagenes?.length > 0 && (
                            <Stack direction='row' spacing={2} sx={{ overflowX: 'auto', pb: 2 }}>
                              {hotelInfo.hotel.hotelImagenes.map((img) => (
                                <div key={img.id} style={{ position: 'relative' }}>
                                  <img
                                    alt={hotelInfo.hotel.name}
                                    className='hotel-image'
                                    onClick={() => setZoomImage(img.imageUrl)}
                                    onMouseEnter={(e) => {
                                      e.target.style.transform = 'scale(1.05)';
                                      e.target.style.filter = 'brightness(0.8)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.target.style.transform = 'scale(1)';
                                      e.target.style.filter = 'brightness(1)';
                                    }}
                                    src={img.imageUrl}
                                    style={{
                                      width: 200,
                                      height: 150,
                                      objectFit: 'cover',
                                      borderRadius: 8,
                                      cursor: 'pointer',
                                      transition: 'transform 0.3s ease, filter 0.3s ease',
                                    }}
                                  />
                                  <ZoomInIcon
                                    className='zoom-icon'
                                    sx={{
                                      position: 'absolute',
                                      top: '50%',
                                      left: '50%',
                                      transform: 'translate(-50%, -50%)',
                                      color: 'white',
                                      fontSize: 32,
                                      opacity: 0,
                                      transition: 'opacity 0.3s ease',
                                      pointerEvents: 'none',
                                    }}
                                  />
                                </div>
                              ))}
                            </Stack>
                          )}
                          <Typography color='textSecondary' variant='body2'>
                            {hotelInfo?.hotel?.description || 'Descripción no disponible'}
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                            <Typography variant='body2'>
                              <strong>Noches:</strong> {hotelInfo?.totalNights || 'No especificado'}
                            </Typography>
                            <Typography variant='body2'>
                              <strong>Régimen:</strong> {hotelInfo?.regime || 'No especificado'}
                            </Typography>
                            <Typography variant='body2'>
                              <strong>Tipo de habitación:</strong>{' '}
                              {hotelInfo?.roomType || 'No especificado'}
                            </Typography>
                          </Stack>
                        </Flex>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Flex>
              </CardContent>
            </Card>
          )}
          {packageInfo?.trips?.length > 0 && (
            <Card className='card-box' sx={{ padding: '1rem', marginBottom: '1rem' }}>
              <CardContent>
                <Typography
                  variant='h6'
                  sx={{
                    marginBottom: '1rem',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  Transporte
                </Typography>
                <Flex style={{ gap: '1rem' }} vertical>
                  {[...packageInfo.trips]
                    .sort((a, b) => {
                      const dateA = parseISO(a.departureDate);
                      const dateB = parseISO(b.departureDate);
                      return isBefore(dateA, dateB) ? -1 : 1;
                    })
                    .map((trip) => {
                      const originCity = trip.originCity?.name || '-';
                      const destinationCity = trip.destinationCity?.name || '-';
                      const isIda = trip.tripType?.description === 'Ida';
                      const departureDate = parseISO(trip.departureDate);
                      const arrivalDate = parseISO(trip.arrivalDate);

                      return (
                        <div
                          key={trip.id || Math.random()}
                          style={{
                            padding: 16,
                            background: '#fafafa',
                            borderRadius: 15,
                            border: '1px solid #e0e0e0',
                          }}
                        >
                          <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                            alignItems='center'
                          >
                            <Stack
                              alignItems='center'
                              direction='row'
                              spacing={1}
                              sx={{
                                width: { xs: '100%', sm: 'auto' },
                                justifyContent: {
                                  xs: 'flex-start',
                                  sm: 'center',
                                },
                                minWidth: 40,
                              }}
                            >
                              {isIda ? (
                                <ArrowForwardIcon
                                  sx={{
                                    color: defaultBackground,
                                    fontSize: 32,
                                    background: '#fafafa',
                                    borderRadius: '50%',
                                    border: `1px solid ${defaultBackground}`,
                                    padding: '4px',
                                  }}
                                />
                              ) : (
                                <ArrowBackIcon
                                  sx={{
                                    color: defaultBackground,
                                    fontSize: 32,
                                    background: '#fafafa',
                                    borderRadius: '50%',
                                    border: `1px solid ${defaultBackground}`,
                                    padding: '4px',
                                  }}
                                />
                              )}
                              <Typography
                                sx={{
                                  display: { xs: 'inline', sm: 'none' },
                                  fontWeight: 600,
                                }}
                                variant='body2'
                              >
                                {isIda ? 'Ida' : 'Vuelta'}
                              </Typography>
                            </Stack>
                            <div style={{ flex: 1, width: '100%' }}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={1}>
                                {trip.company && (
                                  <Typography variant='body2'>
                                    <strong>Compañía:</strong> {trip.company}
                                  </Typography>
                                )}
                                <Typography variant='body2'>
                                  <strong>Transporte:</strong>{' '}
                                  {trip.transportType?.description || '-'}
                                </Typography>
                                {trip.travelCode && (
                                  <Typography variant='body2'>
                                    <strong>Código:</strong> {trip.travelCode}
                                  </Typography>
                                )}
                              </Stack>
                              <Flex
                                style={{
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  width: '100%',
                                }}
                              >
                                {isValid(departureDate) && isValid(arrivalDate) ? (
                                  <>
                                    <Typography fontWeight={700} variant='h6'>
                                      {formatWithMomentFormat(departureDate, 'DD MMM. HH:mm')}
                                    </Typography>
                                    <Typography
                                      fontWeight={700}
                                      sx={{
                                        textAlign: { xs: 'right', sm: 'left' },
                                      }}
                                      variant='h6'
                                    >
                                      {formatWithMomentFormat(arrivalDate, 'DD MMM. HH:mm')}
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography
                                    fontWeight={700}
                                    sx={{ width: '100%', textAlign: 'center' }}
                                    variant='h6'
                                  >
                                    -
                                  </Typography>
                                )}
                              </Flex>
                              <Flex
                                style={{
                                  alignItems: 'center',
                                  margin: '0.5rem 0',
                                  position: 'relative',
                                }}
                              >
                                <div
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: '#1976d2',
                                    marginRight: 4,
                                  }}
                                />
                                <div
                                  style={{
                                    flex: 1,
                                    borderTop: '1px dashed #bdbdbd',
                                    height: 0,
                                    position: 'relative',
                                    top: -5,
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  {Array.isArray(trip.stopDetails) &&
                                    trip.stopDetails.length > 0 &&
                                    trip.stopDetails.map((stop, idx) => {
                                      const hours =
                                        typeof stop.waitingHours === 'number'
                                          ? stop.waitingHours
                                          : 0;
                                      const minutes =
                                        typeof stop.waitingMinutes === 'number'
                                          ? stop.waitingMinutes
                                          : 0;
                                      const duration = `${
                                        hours > 0 ? `${hours}h ` : ''
                                      }${minutes}m`;
                                      const location = stop.localidad?.name || 'Escala';
                                      return (
                                        <Tooltip
                                          key={idx}
                                          title={`${location} - Duración escala: ${duration}`}
                                          arrow
                                        >
                                          <div
                                            style={{
                                              position: 'absolute',
                                              left: `${
                                                ((idx + 1) / (trip.stopDetails.length + 1)) * 100
                                              }%`,
                                              transform: 'translate(-50%, -50%)',
                                              top: 0,
                                              width: 10,
                                              height: 10,
                                              borderRadius: '50%',
                                              background: '#ff9800',
                                              cursor: 'pointer',
                                            }}
                                          />
                                        </Tooltip>
                                      );
                                    })}
                                </div>
                                <div
                                  style={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background: '#1976d2',
                                    marginLeft: 4,
                                  }}
                                />
                              </Flex>
                              <Flex
                                style={{
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-end',
                                }}
                              >
                                <div style={{ textAlign: 'left' }}>
                                  <Typography color='#bdbdbd' variant='caption'>
                                    {originCity}
                                  </Typography>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                  <Typography color='#bdbdbd' variant='caption'>
                                    {destinationCity}
                                  </Typography>
                                </div>
                              </Flex>
                            </div>
                          </Stack>
                        </div>
                      );
                    })}
                </Flex>
              </CardContent>
            </Card>
          )}
          {packageDescription?.included && (
            <PackageAccordion content={packageDescription.included} title='Servicios Incluidos' />
          )}
          {packageDescription?.notIncluded && (
            <PackageAccordion
              title='Servicios No Incluidos'
              content={packageDescription.notIncluded}
            />
          )}
          {packageDescription?.observations && (
            <PackageAccordion
              title='Información Importante'
              content={packageDescription.observations}
            />
          )}
        </Flex>

        <Flex className='boxes-wrapper package-details' vertical>
          <Flex className='package-details-cards' vertical>
            <Flex className='card-box package-details__summary-card' vertical>
              <Flex className='package-details__summary-card__header with-bottom-divider'>
                <h3 className='package-information__summary-title'>
                  {t('packages.details.package_detail')}
                </h3>
              </Flex>
              <Flex className='package-details__summary-card__content' vertical>
                <div className='package-details__section'>
                  <h4 className='package-details__section-title'>Destinos:</h4>
                  <div className='package-destinations'>
                    <ul className='package-destinations-list'>
                      {packageData?.paqueteDestinos?.map((destino) => (
                        <li className='package-destination' key={destino?.id || Math.random()}>
                          <span className='package-destination__city'>
                            {typeof destino?.city?.name === 'string'
                              ? destino.city.name
                              : 'Ciudad no especificada'}
                          </span>
                          <span className='package-destination__country'>
                            (
                            {typeof destino?.city?.country?.name === 'string'
                              ? destino.city.country.name
                              : 'País no especificado'}
                            )
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className='package-details__section'>
                  <div className='package-details__info-row'>
                    <h4 className='package-details__info-label'>Desde:</h4>
                    <span className='package-details__info-value'>
                      {packageData?.originCity?.name || 'Ciudad no especificada'}
                    </span>
                  </div>
                  <div className='package-details__info-row'>
                    <h4 className='package-details__info-label'>Salidas:</h4>
                    <span className='package-details__info-value'>{formattedDates}</span>
                  </div>
                  <div className='package-details__info-row'>
                    <h4 className='package-details__info-label'>Noches:</h4>
                    <span className='package-details__info-value'>
                      {packageData?.totalNights ?? '-'}
                    </span>
                  </div>
                </div>

                {includedItems.length > 0 && (
                  <div className='package-details__section'>
                    <DynamicIncludesRow paquetesIncluidos={includedItems} fullWidth />
                  </div>
                )}
              </Flex>
            </Flex>

            <Flex className='card-box package-details__payment-card' vertical>
              <Flex className='package-details__payment-card__header with-bottom-divider'>
                <p>Precio total</p>
                <p style={{ fontWeight: 600, fontSize: '1.125rem' }}>
                  {selectedPrice
                    ? getFinalPrice({
                        currencyCode,
                        total: selectedPrice.totalAmount,
                      })
                    : `${currencyCode} 0`}
                </p>
              </Flex>
              <Flex className='package-details__payment-card__content with-bottom-divider' vertical>
                <PaymentSummaryRow
                  label='Precio del producto'
                  value={
                    selectedPrice
                      ? getFinalPrice({
                          currencyCode,
                          total: selectedPrice.amount,
                        })
                      : `${currencyCode} 0`
                  }
                />
                <PaymentSummaryRow
                  label='Impuestos y tasas'
                  value={
                    selectedPrice
                      ? getFinalPrice({
                          currencyCode,
                          total: selectedPrice.taxFree,
                        })
                      : `${currencyCode} 0`
                  }
                />
              </Flex>
              <div className='package-details__payment-card__notice'>
                <div className='title'>Nota</div>
                <div className='content'>
                  PARA TARIFAS EXPRESADAS EN {currencyCode}. EN CASO DE ABONAR EN PESOS (ARS) SE
                  APLICARÁ EL TIPO DE CAMBIO CORRESPONDIENTE AL DÍA DE PAGO, Y SE ADICIONARÁ LA
                  PERCEPCIÓN CORRESPONDIENTE (RG 4815).
                </div>
              </div>
            </Flex>
          </Flex>

          <Flex className='package-details-buttons' style={{ gap: '1rem' }}>
            <button
              className='package-details-button package-details__ask-button'
              onClick={() => {
                setConsultaPackage({
                  ...packageData,
                  title:
                    packageData?.title || packageData?.paqueteInformacion?.[0]?.title || 'Paquete',
                  id: packageData?.id || props.match.params.id,
                  externalId: packageData?.externalId || packageData?.id || props.match.params.id,
                  isInternal: true,
                  originCity: {
                    name: packageData?.originCity?.name || 'Ciudad no especificada',
                  },
                  paqueteDestinos: packageData?.paqueteDestinos || [],
                  hotelInformation: [
                    {
                      ...hotelInfo,
                      roomType: hotelInfo?.roomType || 'STANDARD',
                      occupation: hotelInfo?.occupation || 1,
                    },
                  ],
                  total: selectedPrice
                    ? getFinalPrice({
                        currencyCode,
                        total: selectedPrice.totalAmount,
                      })
                    : `${currencyCode} 0`,
                  providerName: packageData?.provider || 'Internal',
                  link: urlIntranet,
                });
              }}
            >
              Consultar
            </button>
            <ShareButton 
              url={urlIntranet}
              title="Compartir paquete"
            />
          </Flex>
        </Flex>
      </Flex>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        open={showSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity='success' sx={{ width: '100%' }}>
          {t('layout:common.link_copied')}
        </Alert>
      </Snackbar>
      <ContactFormModal
        consultaPackage={consultaPackage}
        onClose={() => setConsultaPackage(undefined)}
        variant='package'
      />
      <Modal
        aria-describedby='modal-description'
        aria-labelledby='modal-title'
        onClose={() => setZoomImage(null)}
        open={!!zoomImage}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '90%',
            maxWidth: '1000px',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #e0e0e0',
              bgcolor: '#f5f5f5',
            }}
          >
            <Typography variant='h6'>Vista ampliada</Typography>
            <IconButton
              onClick={() => setZoomImage(null)}
              sx={{
                color: '#666',
                '&:hover': {
                  color: '#000',
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              overflow: 'auto',
            }}
          >
            <img
              alt='Imagen ampliada'
              src={zoomImage}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 4,
              }}
            />
          </Box>
        </Box>
      </Modal>
    </Flex>
  );
};

export default InternalPackageDetail;
